import React from 'react';
import PropTypes from 'prop-types';
import { Button, Table } from 'antd';
import { connect } from 'dva';
import { FormattedMessage, injectIntl } from 'react-intl';
import ls from 'local-storage';

class AjaxTable extends React.Component {
  static DEFAULT_OPTIONS = {
    page: 1, sorting: 'id,desc',
  };

  static DEFAULT_PER_PAGE = 20;

  static ENDLESS_PAGINATOR_GO_AHEAD = 1;

  constructor(props) {
    super(props);
    this.tableScrollAnchor = React.createRef();

    // respect grid specifics, otherwise localStorage
    const pageSize = (this.props.pagination && this.props.pagination.pageSize) || this.getPerPageFromStore();
    this.state = {
      loading: false,
      enableEndlessPagination: this.props.enableEndlessPagination || false, // appends instead of replace when new data is paginated
      sorting: (this.props.defaultParams && this.props.defaultParams.sorting) ? this.props.defaultParams.sorting : AjaxTable.DEFAULT_OPTIONS.sorting,
      dataSource: [],
      pagination: {
        total: 0,
        current: 1,
        hideOnSinglePage: false,
        showSizeChanger: true,
        pageSize,
        showTotal: (total, range) => this.props.intl.formatMessage({ id: 'Common.Paginator.Total' }, {
          from: range[0], to: range[1], total,
        }),
      },
    };
  }

  componentDidMount() {
    const psf = this.getPsf(this.state, true);
    this.reloadList(psf, false);
  }

  componentWillUpdate(nextProps, nextState) {
    const shouldAppendData = this.state.enableEndlessPagination && nextState.pagination.current !== this.state.pagination.current;
    const shouldRefresh = (nextProps.updateKey !== this.props.updateKey);
    const shouldUpdate = shouldRefresh || shouldAppendData;

    if (nextProps.replaceRecords !== this.props.replaceRecords) {
      this.replaceRecordsHandler(nextProps.replaceRecords);
      return;
    }

    if (shouldUpdate === false) {
      return;
    }

    this.reloadList(this.getPsf(nextState, shouldRefresh, { keepPage: nextProps.keepPage }), shouldAppendData);
  }

  onChange(pagination, filters, sorter) {
    const { pageSize } = this.state.pagination;

    // if grid has custom value, do not overrite it in local storage
    if (pageSize && !(this.props.pagination && this.props.pagination.pageSize) && pageSize !== pagination.pageSize) {
      this.setPerPageToStore(pagination.pageSize);
    }

    const sorting = sorter.field ? `${sorter.field},${sorter.order.replace('end', '')}` : this.state.sorting;

    this.reloadList({
      page: pagination.current || this.state.pagination.current,
      limit: Number(pagination.pageSize || this.state.pagination.pageSize) + (this.state.enableEndlessPagination ? AjaxTable.ENDLESS_PAGINATOR_GO_AHEAD : 0),
      sorting,
    });
  }

  getPsf(state, shouldRefresh, options = { keepPage: false }) {
    const page = shouldRefresh && options.keepPage === false ? 1 : state.pagination.current;
    const limit = this.props.enableEndlessPagination ? (state.pagination.pageSize || 0) + AjaxTable.ENDLESS_PAGINATOR_GO_AHEAD : state.pagination.pageSize;

    return {
      limit,
      sorting: state.sorting,
      page,
      skip: this.props.enableEndlessPagination ? (limit - AjaxTable.ENDLESS_PAGINATOR_GO_AHEAD) * (page - 1) : limit * (page - 1),
    };
  }

  getPerPageStoreKey() {
    return 'LXP_PAGINATOR_PER_PAGE';
  }

  setPerPageToStore(value) {
    return ls.set(this.getPerPageStoreKey(), value);
  }

  getPerPageFromStore() {
    return ls.get(this.getPerPageStoreKey()) || AjaxTable.DEFAULT_PER_PAGE;
  }

  pageChange = () => {
    this.tableScrollAnchor.current.scrollIntoView({ behavior: 'auto', block: 'start', alignToTop: true });
  }

  replaceRecordsHandler = (records) => {
    const dataSource = this.state.dataSource.map((item) => {
      const key = item[this.props.rowKey];
      const newRecord = records.find(record => record[this.props.rowKey] === key);
      if (newRecord) {
        return newRecord;
      }

      return item;
    });

    this.setState({
      dataSource,
    });
  }

  reloadList(psf, append = false) {
    this.setState({ loading: true });
    this.props.getData(psf).then((response) => {
      if (!response) {
        return;
      }

      const { data, total } = response;

      // set to grid right number of items, go ahead must affect only "total"
      let gridItems = data.slice(0, psf.limit);
      gridItems = append ? this.state.dataSource.concat(gridItems) : gridItems;

      this.setState({
        dataSource: gridItems,
        loading: false,
        pagination: {
          onChange: this.pageChange,
          ...this.state.pagination,
          current: Number(psf.page),
          pageSize: Number(psf.limit),
          total: this.state.enableEndlessPagination ? append ? this.state.dataSource.concat(data).length : data.length : total,
        },
      });
    });
  }

  loadMore() {
    this.setState({
      pagination: {
        ...this.state.pagination, current: this.state.pagination.current + 1,
      },
    });
  }

  render() {
    const loading = this.props.loading !== undefined ? this.props.loading : this.state.loading;

    return (<div style={{ position: 'relative' }}>
      <div ref={this.tableScrollAnchor} style={{ position: 'absolute', top: -40, left: 0 }} />
      <Table
        loading={loading}
        dataSource={this.props.dataSource || this.state.dataSource}
        rowKey={this.props.rowKey}
        pagination={this.state.enableEndlessPagination === false ? this.state.pagination : false}
        scroll={{ x: true }}
        onChange={this.onChange.bind(this)}
        className={this.props.className}
        columns={this.props.columns}
      >
        {this.props.children}
      </Table>

      {this.state.enableEndlessPagination && this.state.pagination.total > this.state.dataSource.length &&
        <div className="text-center btn-wrap">
          <Button type="primary" onClick={this.loadMore.bind(this)}>
            <FormattedMessage id="Common.LoadMore" />
          </Button>
        </div>}
    </div>);
  }
}

AjaxTable.defaultProps = {
  keepPage: false,
};

AjaxTable.propTypes = {
  // means on next refresh the page will stay the same
  keepPage: PropTypes.bool,
  loading: PropTypes.bool,
  updateKey: PropTypes.string, // on change of the value, grid update the data
  replaceRecords: PropTypes.arrayOf(PropTypes.object), // grid listen to this property and replace record in grid
};

export default connect(({ app }) => ({ app }))(injectIntl(AjaxTable));



// WEBPACK FOOTER //
// ./src/components/AjaxTable/AjaxTable.js